import _objectSpread from "@babel/runtime-corejs3/helpers/objectSpread2";
import _toConsumableArray from "@babel/runtime-corejs3/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime-corejs3/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime-corejs3/helpers/objectWithoutProperties";
var _excluded = ["store"];
import "requestidlecallback";
import { useDebugValue, useEffect, useRef } from "react";
import isObject from "lodash/isObject";
import isFunction from "lodash/isFunction";
import { warning, isPageVisible, mergeWithDefined } from "@lilib/utils";
import useUpdate from "./useUpdate";
import usePersist from "./usePersist";
import useUnmount from "./useUnmount";
import useSetState from "./useSetState";
import useThrottle from "./useThrottle";
import useLatestRef from "./useLatestRef";
import useMountedRef from "./useMountedRef";
import useUnmountedRef from "./useUnmountedRef";
import { useCacheConfig } from "./configs/CacheConfig";
import { useLoadConfig } from "./configs/LoadConfig";
function useLoad(callback) {
  var deps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var _useCacheConfig = useCacheConfig(),
    cache = _useCacheConfig.cache;
  var _useLoadConfig = useLoadConfig(),
    store = _useLoadConfig.store,
    config = _objectWithoutProperties(_useLoadConfig, _excluded);
  var _mergeWithDefined = mergeWithDefined(config, options),
    key = _mergeWithDefined.key,
    idle = _mergeWithDefined.idle,
    imperative = _mergeWithDefined.imperative,
    independent = _mergeWithDefined.independent,
    fallback = _mergeWithDefined.fallback,
    initialData = _mergeWithDefined.initialData,
    defaultParams = _mergeWithDefined.defaultParams,
    cacheKey = _mergeWithDefined.cacheKey,
    cacheTime = _mergeWithDefined.cacheTime,
    staleTime = _mergeWithDefined.staleTime,
    cacheValidate = _mergeWithDefined.cacheValidate,
    retry = _mergeWithDefined.retry,
    retryLimit = _mergeWithDefined.retryLimit,
    retryInterval = _mergeWithDefined.retryInterval,
    fallbackRetry = _mergeWithDefined.fallbackRetry,
    fallbackRetryLimit = _mergeWithDefined.fallbackRetryLimit,
    fallbackRetryInterval = _mergeWithDefined.fallbackRetryInterval,
    polling = _mergeWithDefined.polling,
    pollingInterval = _mergeWithDefined.pollingInterval,
    pollingInPageHiding = _mergeWithDefined.pollingInPageHiding,
    pollingIntervalInPageHiding = _mergeWithDefined.pollingIntervalInPageHiding,
    autoReloadWaitTime = _mergeWithDefined.autoReloadWaitTime,
    autoReloadOnPageShow = _mergeWithDefined.autoReloadOnPageShow,
    autoReloadOnWindowFocus = _mergeWithDefined.autoReloadOnWindowFocus,
    autoReloadOnNetworkReconnect = _mergeWithDefined.autoReloadOnNetworkReconnect;
  var isAssociated = key != null && !independent;
  var getCachedData = usePersist(function () {
    if (cacheKey != null && cache.has(cacheKey)) {
      var cachedData = cache.get(cacheKey);
      if (cacheValidate) {
        return cacheValidate(cachedData) ? cachedData : initialData;
      } else {
        return cachedData;
      }
    } else {
      return initialData;
    }
  });
  var _useSetState = useSetState(function () {
      return {
        data: getCachedData(),
        error: null,
        loading: !imperative
      };
    }),
    _useSetState2 = _slicedToArray(_useSetState, 2),
    state = _useSetState2[0],
    setState = _useSetState2[1];
  var mountedRef = useMountedRef();
  var unmountedRef = useUnmountedRef();
  var callbackRef = useLatestRef(callback);
  var fallbackRef = useLatestRef(fallback);
  var idRef = useRef(0);
  var readyRef = useRef(false);
  var forceRef = useRef(false);
  var paramsRef = useRef();
  var loadingRef = useRef();
  var unlistenRef = useRef();
  var idleTimerRef = useRef(0);
  var retryTimerRef = useRef(0);
  var pollingTimerRef = useRef(0);
  var clearPending = usePersist(function () {
    idRef.current++;
    readyRef.current = false;
    clearTimeout(retryTimerRef.current);
    clearTimeout(pollingTimerRef.current);
    cancelAutoReload();
    cancelIdleCallback(idleTimerRef.current);
    if (loadingRef.current) {
      var _loadingRef$current = loadingRef.current,
        _key = _loadingRef$current.key,
        _store = _loadingRef$current.store;
      if (_store.isLoading(_key, load)) {
        _store.deleteLoading(_key, load);
        _store["for"](_key).emit("cancel");
      }
      loadingRef.current = undefined;
    }
    if (unlistenRef.current) {
      unlistenRef.current();
      unlistenRef.current = undefined;
    }
  });
  var triggerSuccess = usePersist(function (data, key) {
    if (options.onSuccess) {
      options.onSuccess(data);
    } else if (config.onSuccess) {
      config.onSuccess(data, key);
    }
    if (config.handleSuccess) {
      config.handleSuccess(data, key);
    }
  });
  var triggerFailure = usePersist(function (error, key) {
    if (options.onFailure) {
      options.onFailure(error);
    } else if (config.onFailure) {
      config.onFailure(error, key);
    }
    if (config.handleFailure) {
      config.handleFailure(error, key);
    }
  });
  var triggerFinally = usePersist(function (key) {
    if (options.onFinally) {
      options.onFinally();
    } else if (config.onFinally) {
      config.onFinally(key);
    }
    if (config.handleFinally) {
      config.handleFinally(key);
    }
  });
  var load = usePersist(function () {
    for (var _len = arguments.length, params = new Array(_len), _key2 = 0; _key2 < _len; _key2++) {
      params[_key2] = arguments[_key2];
    }
    clearPending();
    readyRef.current = true;
    paramsRef.current = params;
    var force = forceRef.current;
    if (force) {
      forceRef.current = false;
    }
    if (!mountedRef.current) {
      if (process.env.NODE_ENV !== "production") {
        warning(true, "You should not load data before the component is mounted.", {
          scope: "useLoad"
        });
      }
      return new Promise(function () {});
    }
    if (!force && staleTime > 0 && cacheKey != null && cache.has(cacheKey) && state.error == null) {
      var _cachedData;
      var timestamp = cache.getCacheTimestamp(cacheKey);
      if (timestamp && Date.now() - timestamp <= staleTime && (state.data !== undefined || (_cachedData = getCachedData()) !== undefined)) {
        var _data = state.data !== undefined ? state.data : _cachedData;
        if (state.data === undefined) {
          setState({
            data: _cachedData,
            loading: false
          });
        } else if (state.loading) {
          setState({
            loading: false
          });
        }
        return new Promise(function (resolve) {
          resolve(_data);
        });
      }
    }
    if (isAssociated && !store.isLoading(key)) {
      store.setLoading(key, load);
      loadingRef.current = {
        key: key,
        load: load,
        store: store
      };
    }
    var cachedData;
    if (state.data === undefined && cacheKey != null && cache.has(cacheKey) && (cachedData = getCachedData()) !== undefined) {
      setState({
        data: cachedData,
        loading: true
      });
    } else if (!state.loading) {
      setState({
        loading: true
      });
    }
    var id = idRef.current;
    var isCanceled = function isCanceled() {
      return id !== idRef.current;
    };
    var checkPolling = function checkPolling() {
      if (readyRef.current) {
        if (isPageVisible()) {
          if (polling) {
            pollingTimerRef.current = +setTimeout(function () {
              if (!isCanceled()) {
                reload();
              }
            }, pollingInterval);
          }
        } else {
          if (pollingInPageHiding) {
            pollingTimerRef.current = +setTimeout(function () {
              if (!isCanceled()) {
                reload();
              }
            }, pollingIntervalInPageHiding);
          }
        }
      }
    };
    if (isAssociated && store.isLoading(key) && !store.isLoading(key, load)) {
      return new Promise(function (resolve, reject) {
        var unlisten = function unlisten() {
          unlistenRef.current = undefined;
          store["for"](key).off("cancel", handleCancel);
          store["for"](key).off("failure", handleReject);
          store["for"](key).off("success", handleResolve);
        };
        var handleCancel = function handleCancel() {
          unlisten();
          if (!isCanceled()) {
            reload();
          }
        };
        var handleResolve = function handleResolve(data) {
          unlisten();
          if (!isCanceled()) {
            resolve(data);
          }
        };
        var handleReject = function handleReject(error) {
          unlisten();
          if (!isCanceled()) {
            reject(error);
          }
        };
        unlistenRef.current = unlisten;
        store["for"](key).on("cancel", handleCancel);
        store["for"](key).on("failure", handleReject);
        store["for"](key).on("success", handleResolve);
      }).then(function (data) {
        if (isCanceled()) {
          return new Promise(function () {});
        }
        triggerSuccess(data, key);
        triggerFinally(key);
        checkPolling();
        return data;
      }, function (error) {
        if (isCanceled()) {
          return new Promise(function () {});
        }
        triggerFailure(error, key);
        triggerFinally(key);
        checkPolling();
        throw error;
      });
    }
    return new Promise(function (resolve, reject) {
      var retryCount = 0;
      var fallbackInvoked = false;
      var fallbackRetryCount = 0;
      var handleReject = function handleReject(error) {
        if (isCanceled()) {
          return;
        }
        if (retry && ++retryCount <= retryLimit) {
          var delay = isFunction(retryInterval) ? retryInterval(retryCount) : retryInterval;
          if (delay > 0) {
            retryTimerRef.current = +setTimeout(function () {
              if (!isCanceled()) {
                callbackRef.current.apply(callbackRef, params).then(resolve, handleReject);
              }
            }, delay);
          } else {
            callbackRef.current.apply(callbackRef, params).then(resolve, handleReject);
          }
        } else if (fallbackRef.current && !fallbackInvoked) {
          fallbackInvoked = true;
          fallbackRef.current.apply(fallbackRef, params).then(resolve, handleReject);
        } else if (fallbackRef.current && fallbackInvoked && fallbackRetry && ++fallbackRetryCount <= fallbackRetryLimit) {
          var _delay = isFunction(fallbackRetryInterval) ? fallbackRetryInterval(fallbackRetryCount) : fallbackRetryInterval;
          if (_delay > 0) {
            retryTimerRef.current = +setTimeout(function () {
              if (!isCanceled()) {
                if (fallbackRef.current) {
                  fallbackRef.current.apply(fallbackRef, params).then(resolve, handleReject);
                } else {
                  reject(error);
                }
              }
            }, _delay);
          } else {
            fallbackRef.current.apply(fallbackRef, params).then(resolve, handleReject);
          }
        } else {
          reject(error);
        }
      };
      if (idle) {
        idleTimerRef.current = requestIdleCallback(function () {
          if (!isCanceled()) {
            callbackRef.current.apply(callbackRef, params).then(resolve, handleReject);
          }
        }, isObject(idle) ? idle : undefined);
      } else {
        callbackRef.current.apply(callbackRef, params).then(resolve, handleReject);
      }
    }).then(function (data) {
      if (isCanceled()) {
        return new Promise(function () {});
      }
      if (cacheKey != null) {
        cache.set(cacheKey, data, {
          cacheTime: cacheTime
        });
      }
      if (isAssociated && store.isLoading(key, load)) {
        store.deleteLoading(key, load);
        store["for"](key).emit("success", data);
        loadingRef.current = undefined;
      }
      setState({
        data: data,
        error: null,
        loading: false
      });
      triggerSuccess(data, key);
      triggerFinally(key);
      checkPolling();
      return data;
    }, function (error) {
      if (isCanceled()) {
        return new Promise(function () {});
      }
      if (isAssociated && store.isLoading(key, load)) {
        store.deleteLoading(key, load);
        store["for"](key).emit("failure", error);
        loadingRef.current = undefined;
      }
      setState({
        error: error,
        loading: false
      });
      triggerFailure(error, key);
      triggerFinally(key);
      checkPolling();
      throw error;
    });
  });
  var force = usePersist(function () {
    forceRef.current = true;
    return load.apply(void 0, arguments);
  });
  var reload = usePersist(function (options) {
    var params = paramsRef.current || defaultParams;
    if (process.env.NODE_ENV !== "production") {
      warning(!!callback.length && params === undefined, "You should provide params by setting `defaultParams` option or calling the `load(...params)` method.", {
        scope: "useLoad"
      });
    }
    forceRef.current = !!options && !!options.force;
    if (params) {
      load.apply(void 0, _toConsumableArray(params));
    } else {
      // @ts-ignore
      load();
    }
  });
  var update = usePersist(function (newData) {
    setState(function (prevState) {
      return {
        data: isFunction(newData) ? newData(prevState.data) : newData
      };
    });
  });
  var cancel = usePersist(function () {
    clearPending();
    forceRef.current = false;
    if (!unmountedRef.current && state.loading) {
      setState({
        loading: false
      });
    }
  });
  var _useThrottle = useThrottle(function () {
      if (readyRef.current && !state.loading) {
        reload();
      }
    }, {
      wait: autoReloadWaitTime,
      leading: true,
      trailing: false
    }),
    _useThrottle2 = _slicedToArray(_useThrottle, 2),
    autoReload = _useThrottle2[0],
    cancelAutoReload = _useThrottle2[1].cancel;
  useEffect(function () {
    if (autoReloadOnPageShow || polling || pollingInPageHiding) {
      var listener = function listener() {
        if (isPageVisible()) {
          if (autoReloadOnPageShow || polling) {
            autoReload();
          }
        } else {
          if (pollingInPageHiding) {
            autoReload();
          }
        }
      };
      document.addEventListener("visibilitychange", listener);
      return function () {
        document.removeEventListener("visibilitychange", listener);
      };
    }
  }, [autoReloadOnPageShow, polling, pollingInPageHiding, autoReload]);
  useEffect(function () {
    if (autoReloadOnWindowFocus) {
      window.addEventListener("focus", autoReload);
      return function () {
        window.removeEventListener("focus", autoReload);
      };
    }
  }, [autoReloadOnWindowFocus, autoReload]);
  useEffect(function () {
    if (autoReloadOnNetworkReconnect) {
      window.addEventListener("online", autoReload);
      return function () {
        window.removeEventListener("online", autoReload);
      };
    }
  }, [autoReloadOnNetworkReconnect, autoReload]);
  useEffect(function () {
    if (isAssociated) {
      var handleSuccess = function handleSuccess(data) {
        if (!loadingRef.current || loadingRef.current.load !== load) {
          setState({
            data: data,
            error: null,
            loading: false
          });
        }
      };
      var handleFailure = function handleFailure(error) {
        if (!loadingRef.current || loadingRef.current.load !== load) {
          setState({
            error: error,
            loading: false
          });
        }
      };
      store["for"](key).on("success", handleSuccess);
      store["for"](key).on("failure", handleFailure);
      return function () {
        store["for"](key).off("success", handleSuccess);
        store["for"](key).off("failure", handleFailure);
      };
    }
  }, [key, isAssociated, store, load, staleTime, setState]);
  useEffect(function () {
    if (key != null) {
      store.addReloader(key, reload);
      return function () {
        store.removeReloader(key, reload);
      };
    }
  }, [key, store, reload]);
  useEffect(function () {
    if (!imperative) {
      reload();
    }
  }, deps // eslint-disable-line
  );

  useUpdate(function () {
    if (process.env.NODE_ENV !== "production") {
      warning(true, "You should provide an immutable value for the `key` option.", {
        scope: "useLoad"
      });
    }
  }, [key]);
  useUnmount(cancel);
  useDebugValue(state);
  return _objectSpread(_objectSpread({
    load: load,
    force: force,
    reload: reload,
    update: update,
    cancel: cancel
  }, state), {}, {
    reloading: state.loading && state.data !== undefined,
    initializing: state.loading && state.data === undefined
  });
}
export default useLoad;